import React, { useState, useEffect } from "react";
import { Modal, Tab, Tabs } from "react-bootstrap";
import {
  useSEOAgent,
  KeywordData,
  ContentBrief,
  TechnicalChecklistItem,
} from "../../../../hooks/useSEOAgent";

interface Props {
  show: boolean;
  handleClose: () => void;
  projectId: string;
  projectTitle: string;
}

export const SEOAgentModal: React.FC<Props> = ({
  show,
  handleClose,
  projectId,
  projectTitle,
}) => {
  const [activeTab, setActiveTab] = useState("generate");
  const [selectedKeyword, setSelectedKeyword] = useState<string | null>(null);
  const [keywordFilter, setKeywordFilter] = useState<"all" | "informational" | "transactional" | "commercial">("all");
  const [briefKeywordInput, setBriefKeywordInput] = useState("");

  const {
    isLoading,
    isGeneratingBrief,
    error,
    analysis,
    currentBrief,
    generateAnalysis,
    generateContentBrief,
    clearError,
    reset,
  } = useSEOAgent();

  useEffect(() => {
    if (show) {
      reset();
      setActiveTab("generate");
      setSelectedKeyword(null);
      setBriefKeywordInput("");
    }
  }, [show, reset]);

  const handleGenerate = async () => {
    await generateAnalysis(projectId);
    setActiveTab("keywords");
  };

  const handleGenerateBrief = async (keyword: string) => {
    setSelectedKeyword(keyword);
    await generateContentBrief(projectId, keyword);
  };

  const handleCustomBrief = async () => {
    if (!briefKeywordInput.trim()) return;
    await generateContentBrief(projectId, briefKeywordInput.trim());
    setBriefKeywordInput("");
  };

  // Badge helpers
  const getIntentBadge = (intent: KeywordData["intent"]) => {
    const config = {
      informational: { color: "info", icon: "bi-info-circle" },
      transactional: { color: "success", icon: "bi-cart" },
      navigational: { color: "secondary", icon: "bi-compass" },
      commercial: { color: "warning", icon: "bi-search" },
    };
    const { color, icon } = config[intent];
    return (
      <span className={`badge bg-light-${color} text-${color}`}>
        <i className={`bi ${icon} me-1`}></i>
        {intent}
      </span>
    );
  };

  const getDifficultyBadge = (difficulty: KeywordData["difficulty"]) => {
    const colors = { low: "success", medium: "warning", high: "danger" };
    return (
      <span className={`badge bg-light-${colors[difficulty]} text-${colors[difficulty]}`}>
        {difficulty}
      </span>
    );
  };

  const getVolumeBadge = (volume: KeywordData["volumeEstimate"]) => {
    const icons = { low: "bi-graph-down", medium: "bi-graph-up", high: "bi-graph-up-arrow" };
    return (
      <span className="text-muted fs-8">
        <i className={`bi ${icons[volume]} me-1`}></i>
        {volume} volume
      </span>
    );
  };

  const getPriorityBadge = (priority: TechnicalChecklistItem["priority"]) => {
    const colors = { high: "danger", medium: "warning", low: "secondary" };
    return (
      <span className={`badge bg-${colors[priority]} text-uppercase fs-9`}>
        {priority}
      </span>
    );
  };

  // Filter keywords by intent
  const filteredPrimaryKeywords = analysis?.keywords.primary.filter(
    (k) => keywordFilter === "all" || k.intent === keywordFilter
  ) || [];
  const filteredLongTailKeywords = analysis?.keywords.longTail.filter(
    (k) => keywordFilter === "all" || k.intent === keywordFilter
  ) || [];

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      backdrop="static"
      scrollable
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bolder">
          <i className="bi bi-search me-2 text-primary"></i>
          SEO Expert Agent
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-3">
        {error && (
          <div className="alert alert-danger mb-4 d-flex align-items-center">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <span className="flex-grow-1">{error}</span>
            <button type="button" className="btn-close" onClick={clearError}></button>
          </div>
        )}

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => k && setActiveTab(k)}
          className="mb-4"
        >
          {/* Generate Tab */}
          <Tab eventKey="generate" title="Generate">
            <div className="py-4 text-center">
              <div className="mb-5">
                <div
                  className="d-inline-flex align-items-center justify-content-center bg-light-primary rounded-circle mb-4"
                  style={{ width: "100px", height: "100px" }}
                >
                  <i className="bi bi-search text-primary fs-1"></i>
                </div>
                <h3 className="fw-bold mb-2">AI SEO Analysis</h3>
                <p className="text-muted mx-auto" style={{ maxWidth: "500px" }}>
                  Generate comprehensive SEO strategy for{" "}
                  <strong>{projectTitle}</strong> including keyword research,
                  on-page optimization, and content briefs.
                </p>
              </div>

              <div className="bg-light-primary rounded p-4 mb-5 mx-auto" style={{ maxWidth: "600px" }}>
                <h6 className="fw-bold text-primary mb-3">
                  <i className="bi bi-magic me-2"></i>
                  What will be generated
                </h6>
                <div className="row text-start">
                  <div className="col-md-6">
                    <ul className="list-unstyled mb-0">
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Primary & long-tail keywords
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Search intent classification
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Keyword difficulty ratings
                      </li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <ul className="list-unstyled mb-0">
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Title & meta suggestions
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Content brief templates
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Technical SEO checklist
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <button
                className="btn btn-primary btn-lg px-5"
                onClick={handleGenerate}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Analyzing...
                  </>
                ) : (
                  <>
                    <i className="bi bi-lightning me-2"></i>
                    Generate SEO Analysis
                  </>
                )}
              </button>
            </div>
          </Tab>

          {/* Keywords Tab */}
          <Tab eventKey="keywords" title="Keywords" disabled={!analysis}>
            {analysis && (
              <div className="py-3">
                {/* Intent Filter */}
                <div className="d-flex gap-2 mb-4">
                  {(["all", "informational", "transactional", "commercial"] as const).map((filter) => (
                    <button
                      key={filter}
                      className={`btn btn-sm ${keywordFilter === filter ? "btn-primary" : "btn-light"}`}
                      onClick={() => setKeywordFilter(filter)}
                    >
                      {filter === "all" ? "All Keywords" : filter.charAt(0).toUpperCase() + filter.slice(1)}
                    </button>
                  ))}
                </div>

                {/* Primary Keywords */}
                <div className="mb-5">
                  <h5 className="fw-bold mb-3">
                    <i className="bi bi-star me-2 text-warning"></i>
                    Primary Keywords ({filteredPrimaryKeywords.length})
                  </h5>
                  <div className="table-responsive">
                    <table className="table table-row-bordered align-middle">
                      <thead>
                        <tr className="fs-7 fw-bold text-muted">
                          <th>Keyword</th>
                          <th>Intent</th>
                          <th>Difficulty</th>
                          <th>Volume</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPrimaryKeywords.map((kw, idx) => (
                          <tr key={idx}>
                            <td className="fw-semibold">{kw.keyword}</td>
                            <td>{getIntentBadge(kw.intent)}</td>
                            <td>{getDifficultyBadge(kw.difficulty)}</td>
                            <td>{getVolumeBadge(kw.volumeEstimate)}</td>
                            <td>
                              <button
                                className="btn btn-sm btn-light-primary"
                                onClick={() => handleGenerateBrief(kw.keyword)}
                                disabled={isGeneratingBrief && selectedKeyword === kw.keyword}
                              >
                                {isGeneratingBrief && selectedKeyword === kw.keyword ? (
                                  <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                  <>
                                    <i className="bi bi-file-text me-1"></i>
                                    Brief
                                  </>
                                )}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Long-Tail Keywords */}
                <div className="mb-5">
                  <h5 className="fw-bold mb-3">
                    <i className="bi bi-list-nested me-2 text-info"></i>
                    Long-Tail Keywords ({filteredLongTailKeywords.length})
                  </h5>
                  <div className="d-flex flex-wrap gap-2">
                    {filteredLongTailKeywords.map((kw, idx) => (
                      <div
                        key={idx}
                        className="badge bg-light text-gray-800 p-3 cursor-pointer"
                        onClick={() => handleGenerateBrief(kw.keyword)}
                        style={{ cursor: "pointer" }}
                        title={`${kw.intent} • ${kw.difficulty} difficulty`}
                      >
                        {kw.keyword}
                        <span className={`ms-2 badge bg-light-${kw.difficulty === "low" ? "success" : kw.difficulty === "medium" ? "warning" : "danger"}`}>
                          {kw.difficulty}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Questions */}
                {analysis.keywords.questions.length > 0 && (
                  <div className="mb-5">
                    <h5 className="fw-bold mb-3">
                      <i className="bi bi-question-circle me-2 text-primary"></i>
                      People Also Ask ({analysis.keywords.questions.length})
                    </h5>
                    <div className="row">
                      {analysis.keywords.questions.map((q, idx) => (
                        <div key={idx} className="col-md-6 mb-2">
                          <div className="bg-light rounded p-3 h-100">
                            <i className="bi bi-chat-left-text me-2 text-muted"></i>
                            {q}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Keyword Gaps */}
                {analysis.keywords.gaps.length > 0 && (
                  <div>
                    <h5 className="fw-bold mb-3">
                      <i className="bi bi-lightning me-2 text-warning"></i>
                      Keyword Opportunities ({analysis.keywords.gaps.length})
                    </h5>
                    <div className="alert alert-warning">
                      <p className="mb-2 fw-semibold">
                        <i className="bi bi-info-circle me-2"></i>
                        Keywords competitors rank for that you might be missing:
                      </p>
                      <ul className="mb-0">
                        {analysis.keywords.gaps.map((gap, idx) => (
                          <li key={idx}>{gap}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Tab>

          {/* On-Page Tab */}
          <Tab eventKey="onpage" title="On-Page" disabled={!analysis}>
            {analysis?.onPage && (
              <div className="py-3">
                {/* Title Suggestions */}
                <div className="mb-5">
                  <h5 className="fw-bold mb-3">
                    <i className="bi bi-type-h1 me-2 text-primary"></i>
                    Title Tag Suggestions
                  </h5>
                  <div className="bg-light rounded p-4">
                    {analysis.onPage.titleSuggestions.map((title, idx) => (
                      <div key={idx} className="d-flex align-items-center mb-3 last-child-mb-0">
                        <div className="flex-grow-1">
                          <code className="fs-6">{title}</code>
                          <span className="text-muted ms-2 fs-8">
                            ({title.length} characters)
                          </span>
                        </div>
                        <button
                          className="btn btn-sm btn-icon btn-light-primary"
                          onClick={() => navigator.clipboard.writeText(title)}
                          title="Copy to clipboard"
                        >
                          <i className="bi bi-clipboard"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Meta Descriptions */}
                <div className="mb-5">
                  <h5 className="fw-bold mb-3">
                    <i className="bi bi-card-text me-2 text-info"></i>
                    Meta Description Suggestions
                  </h5>
                  <div className="bg-light rounded p-4">
                    {analysis.onPage.metaDescriptions.map((desc, idx) => (
                      <div key={idx} className="mb-4 pb-4 border-bottom last-child-border-0">
                        <p className="mb-2">{desc}</p>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className={`fs-8 ${desc.length > 160 ? "text-danger" : desc.length > 120 ? "text-warning" : "text-success"}`}>
                            {desc.length} / 160 characters
                          </span>
                          <button
                            className="btn btn-sm btn-light-primary"
                            onClick={() => navigator.clipboard.writeText(desc)}
                          >
                            <i className="bi bi-clipboard me-1"></i>
                            Copy
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Header Structure */}
                <div className="mb-5">
                  <h5 className="fw-bold mb-3">
                    <i className="bi bi-list-ol me-2 text-success"></i>
                    Recommended Header Structure
                  </h5>
                  <div className="bg-light rounded p-4">
                    {analysis.onPage.headerStructure.map((header, idx) => (
                      <div
                        key={idx}
                        className="d-flex align-items-center mb-2"
                        style={{ paddingLeft: header.level === "h2" ? "20px" : header.level === "h3" ? "40px" : "0" }}
                      >
                        <span className={`badge bg-${header.level === "h1" ? "primary" : header.level === "h2" ? "info" : "secondary"} me-2`}>
                          {header.level.toUpperCase()}
                        </span>
                        <span>{header.text}</span>
                        {header.keywords && header.keywords.length > 0 && (
                          <span className="ms-2 text-muted fs-8">
                            ({header.keywords.join(", ")})
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Schema Recommendations */}
                {analysis.onPage.schemaRecommendations.length > 0 && (
                  <div>
                    <h5 className="fw-bold mb-3">
                      <i className="bi bi-code-slash me-2 text-warning"></i>
                      Schema Markup Recommendations
                    </h5>
                    <div className="row">
                      {analysis.onPage.schemaRecommendations.map((schema, idx) => (
                        <div key={idx} className="col-md-6 mb-3">
                          <div className="card h-100">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6 className="fw-bold mb-0">{schema.type}</h6>
                                {getPriorityBadge(schema.priority)}
                              </div>
                              <p className="text-muted fs-7 mb-0">{schema.reason}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Tab>

          {/* Content Briefs Tab */}
          <Tab eventKey="briefs" title="Content Briefs" disabled={!analysis}>
            <div className="py-3">
              {/* Generate Custom Brief */}
              <div className="bg-light rounded p-4 mb-5">
                <h6 className="fw-bold mb-3">
                  <i className="bi bi-plus-circle me-2 text-primary"></i>
                  Generate Content Brief
                </h6>
                <div className="d-flex gap-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter target keyword..."
                    value={briefKeywordInput}
                    onChange={(e) => setBriefKeywordInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleCustomBrief()}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={handleCustomBrief}
                    disabled={!briefKeywordInput.trim() || isGeneratingBrief}
                  >
                    {isGeneratingBrief ? (
                      <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                      <>
                        <i className="bi bi-lightning me-1"></i>
                        Generate
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Current Brief Display */}
              {currentBrief && (
                <div className="card mb-4 border-primary">
                  <div className="card-header bg-light-primary border-0">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0 fw-bold">
                        <i className="bi bi-file-text me-2"></i>
                        {currentBrief.targetKeyword}
                      </h5>
                      <span className="badge bg-primary">{currentBrief.suggestedWordCount} words</span>
                    </div>
                  </div>
                  <div className="card-body">
                    {/* Secondary Keywords */}
                    <div className="mb-4">
                      <h6 className="fw-bold text-muted mb-2">Secondary Keywords</h6>
                      <div className="d-flex flex-wrap gap-2">
                        {currentBrief.secondaryKeywords.map((kw, idx) => (
                          <span key={idx} className="badge bg-light text-gray-700">{kw}</span>
                        ))}
                      </div>
                    </div>

                    {/* Target Audience & Tone */}
                    <div className="row mb-4">
                      <div className="col-md-6">
                        <h6 className="fw-bold text-muted mb-2">Target Audience</h6>
                        <p className="mb-0">{currentBrief.targetAudience}</p>
                      </div>
                      <div className="col-md-6">
                        <h6 className="fw-bold text-muted mb-2">Tone</h6>
                        <p className="mb-0">{currentBrief.tone}</p>
                      </div>
                    </div>

                    {/* Outline */}
                    <div className="mb-4">
                      <h6 className="fw-bold text-muted mb-2">Content Outline</h6>
                      <div className="bg-light rounded p-3">
                        {currentBrief.outline.map((section, idx) => (
                          <div key={idx} className="mb-3">
                            <div className="d-flex align-items-center mb-1">
                              <span className={`badge bg-${section.level === "h2" ? "primary" : "secondary"} me-2`}>
                                {section.level.toUpperCase()}
                              </span>
                              <strong>{section.heading}</strong>
                              {section.suggestedWordCount && (
                                <span className="text-muted ms-2 fs-8">~{section.suggestedWordCount} words</span>
                              )}
                            </div>
                            <ul className="mb-0 ps-4">
                              {section.keyPoints.map((point, pIdx) => (
                                <li key={pIdx} className="text-muted fs-7">{point}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Key Points */}
                    <div className="mb-4">
                      <h6 className="fw-bold text-muted mb-2">Key Points to Cover</h6>
                      <ul className="mb-0">
                        {currentBrief.keyPointsToCover.map((point, idx) => (
                          <li key={idx}>{point}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Competitor Insights */}
                    {currentBrief.competitorInsights.length > 0 && (
                      <div className="mb-4">
                        <h6 className="fw-bold text-muted mb-2">
                          <i className="bi bi-eye me-1"></i>
                          Competitor Insights
                        </h6>
                        <ul className="mb-0">
                          {currentBrief.competitorInsights.map((insight, idx) => (
                            <li key={idx}>{insight}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Link Suggestions */}
                    <div className="row">
                      <div className="col-md-6">
                        <h6 className="fw-bold text-muted mb-2">Internal Links</h6>
                        <ul className="mb-0 fs-7">
                          {currentBrief.internalLinkSuggestions.map((link, idx) => (
                            <li key={idx}>{link}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="col-md-6">
                        <h6 className="fw-bold text-muted mb-2">External Links</h6>
                        <ul className="mb-0 fs-7">
                          {currentBrief.externalLinkSuggestions.map((link, idx) => (
                            <li key={idx}>{link}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="card-footer bg-light border-0">
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          const briefText = `# Content Brief: ${currentBrief.targetKeyword}\n\n` +
                            `**Word Count:** ${currentBrief.suggestedWordCount}\n\n` +
                            `**Secondary Keywords:** ${currentBrief.secondaryKeywords.join(", ")}\n\n` +
                            `**Target Audience:** ${currentBrief.targetAudience}\n\n` +
                            `**Tone:** ${currentBrief.tone}\n\n` +
                            `## Outline\n\n` +
                            currentBrief.outline.map(s => `### ${s.heading}\n${s.keyPoints.map(p => `- ${p}`).join("\n")}`).join("\n\n");
                          navigator.clipboard.writeText(briefText);
                        }}
                      >
                        <i className="bi bi-clipboard me-1"></i>
                        Copy Brief
                      </button>
                      <button className="btn btn-light-success">
                        <i className="bi bi-file-earmark-arrow-down me-1"></i>
                        Export Markdown
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Briefs List */}
              {analysis?.contentBriefs && analysis.contentBriefs.length > 0 && (
                <div>
                  <h5 className="fw-bold mb-3">
                    <i className="bi bi-collection me-2"></i>
                    Saved Briefs ({analysis.contentBriefs.length})
                  </h5>
                  <div className="row">
                    {analysis.contentBriefs.map((brief, idx) => (
                      <div key={idx} className="col-md-6 mb-3">
                        <div className="card h-100 cursor-pointer" onClick={() => setSelectedKeyword(brief.targetKeyword)}>
                          <div className="card-body">
                            <h6 className="fw-bold mb-2">{brief.targetKeyword}</h6>
                            <div className="d-flex gap-2 flex-wrap">
                              <span className="badge bg-light text-gray-700">{brief.suggestedWordCount} words</span>
                              <span className="badge bg-light text-gray-700">{brief.outline.length} sections</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Tab>

          {/* Technical Tab */}
          <Tab eventKey="technical" title="Technical" disabled={!analysis}>
            {analysis?.technicalChecklist && (
              <div className="py-3">
                <div className="alert alert-info mb-4">
                  <i className="bi bi-info-circle me-2"></i>
                  Review this checklist to ensure your website follows SEO best practices.
                </div>

                {/* Group by category */}
                {(["speed", "mobile", "structure", "indexing", "security"] as const).map((category) => {
                  const items = analysis.technicalChecklist.filter((item) => item.category === category);
                  if (items.length === 0) return null;

                  const categoryConfig = {
                    speed: { icon: "bi-speedometer2", title: "Page Speed", color: "warning" },
                    mobile: { icon: "bi-phone", title: "Mobile Optimization", color: "info" },
                    structure: { icon: "bi-diagram-3", title: "Site Structure", color: "primary" },
                    indexing: { icon: "bi-search", title: "Indexing & Crawling", color: "success" },
                    security: { icon: "bi-shield-check", title: "Security", color: "danger" },
                  };
                  const config = categoryConfig[category];

                  return (
                    <div key={category} className="mb-5">
                      <h5 className="fw-bold mb-3">
                        <i className={`bi ${config.icon} me-2 text-${config.color}`}></i>
                        {config.title}
                      </h5>
                      <div className="bg-light rounded p-3">
                        {items.map((item, idx) => (
                          <div key={idx} className="d-flex align-items-start mb-3 pb-3 border-bottom last-child-border-0">
                            <div className="me-3 mt-1">
                              <i className="bi bi-check-circle text-success fs-5"></i>
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-start">
                                <strong>{item.item}</strong>
                                {getPriorityBadge(item.priority)}
                              </div>
                              <p className="text-muted mb-0 fs-7">{item.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <button className="btn btn-light" onClick={handleClose}>
          Close
        </button>
        {analysis && (
          <button
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={isLoading}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            Regenerate
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
